import { useEffect, useRef } from 'react'

import { MaybePromise } from '@/models'

import { useForceRerender } from './use-force-rerender'

export const DEFAULT_DEBOUNCE_DELAY = 400

export type DebounceType = ReturnType<typeof setTimeout> | null

export type DebounceFunctionType = (
  callback: () => MaybePromise<void>,
  dynamicDelay?: number,
) => void

export type UseDebounceReturnType = {
  debounce: DebounceFunctionType
  reset: VoidFunction
  active: boolean
}

export type UseDebounceProps = {
  delay?: number
  /**
   * By default, a debounced callback will not be executed if the component is unmounted.
   * If you want to execute the callback even if the component is unmounted, set this to `false`.
   */
  unmountCleanup?: boolean
}

export const useDebounce = (props?: UseDebounceProps): UseDebounceReturnType => {
  const { delay = DEFAULT_DEBOUNCE_DELAY, unmountCleanup = true } = props || {}

  const { forceRerender } = useForceRerender()

  const shouldRerenderActive = useRef(false)
  const timer = useRef<DebounceType>(null)

  const rerenderActive = () => {
    if (shouldRerenderActive.current) forceRerender()
  }

  const reset = () => {
    if (timer.current !== null) clearTimeout(timer.current)
    timer.current = null
  }

  const debounce: DebounceFunctionType = (callback, dynamicDelay) => {
    const time = dynamicDelay ?? delay
    reset()
    timer.current = setTimeout(() => {
      timer.current = null
      callback()
      rerenderActive()
    }, time)
    rerenderActive()
  }

  useEffect(() => {
    return () => {
      if (unmountCleanup) reset()
    }
  }, [unmountCleanup])

  return {
    get active() {
      shouldRerenderActive.current = true
      return !!timer.current
    },
    debounce,
    reset,
  }
}
